// Typed wrappers around the single review write surface,
// `POST /api/agent/reviewables/decisions`. `submitReviewDecisions` is the
// batch-native primitive — it commits any number of staged decisions
// (approve / reject / request-revision, over artefacts and/or tasks) in one
// request. The three named wrappers are thin shims that submit a single-element
// batch and map the per-item result back to the `StaffActionResult` shape every
// existing caller already consumes (`if (result.ok) ...`).

import type {
    ReviewableKind,
    ReviewDecision,
    ReviewDecisionsResponse,
    StaffActionResult,
} from '@septima/contract';
import { setLastRequestId, trackError } from '@/lib/analytics/client';
import { api } from './api';

export type { ReviewDecision } from '@septima/contract';

export interface BatchReviewResult {
    // Transport-level success (the request reached the handler and returned 200).
    // Per-decision outcomes live in `results`; inspect those for partial failure.
    ok: boolean;
    results: ReviewDecisionsResponse['results'];
    summary: { confirmed: number; failed: number };
    invalidate?: string[];
    // Set only on transport-level failure (non-200).
    error?: string;
}

export async function submitReviewDecisions(
    decisions: ReviewDecision[],
): Promise<BatchReviewResult> {
    const res = await api.agent.reviewDecisions({ body: { decisions } });
    // Mirror runStaffEndpoint's X-Request-Id capture so a follow-up frontend
    // error row shares the server's correlation id (PRD #345 / ADR-0021).
    try {
        setLastRequestId(res.headers?.get?.('x-request-id') ?? null);
    } catch {
        /* ignore */
    }
    if (res.status === 200) {
        const body = res.body;
        return {
            ok: true,
            results: body.results,
            summary: body.summary,
            invalidate: body.invalidate,
        };
    }
    const body = res.body as { error?: string } | undefined;
    const error = body?.error ?? `Request failed (status ${res.status})`;
    trackError({ name: 'ApiError', message: error, status: res.status });
    return {
        ok: false,
        results: [],
        summary: { confirmed: 0, failed: decisions.length },
        error,
    };
}

/**
 * Collapse a single-decision batch result into the `StaffActionResult` the
 * named wrappers' callers expect. A per-item failure surfaces as `{ok:false}`
 * with the item's own error message.
 */
function singleToStaffResult(batch: BatchReviewResult): StaffActionResult {
    if (!batch.ok) return { ok: false, error: batch.error ?? 'Review failed' };
    const first = batch.results[0];
    if (!first || !first.ok) return { ok: false, error: first?.error ?? 'Review failed' };
    return { ok: true, data: first.data, invalidate: batch.invalidate };
}

export async function approveReviewable(
    kind: ReviewableKind,
    id: string,
    extra: Record<string, unknown> = {},
): Promise<StaffActionResult> {
    return singleToStaffResult(
        await submitReviewDecisions([{ target: 'artefact', kind, id, decision: 'approve', extra }]),
    );
}

export async function rejectReviewable(
    kind: ReviewableKind,
    id: string,
    reason?: string,
): Promise<StaffActionResult> {
    return singleToStaffResult(
        await submitReviewDecisions([{ target: 'artefact', kind, id, decision: 'reject', reason }]),
    );
}

export async function requestRevisionReviewable(
    kind: ReviewableKind,
    id: string,
    revisionReason: string,
): Promise<StaffActionResult> {
    return singleToStaffResult(
        await submitReviewDecisions([
            { target: 'artefact', kind, id, decision: 'request_revision', reason: revisionReason },
        ]),
    );
}
